type LogLevel = 'silent' | 'error' | 'warn' | 'info' | 'debug' | 'trace';

export interface LoggerConfig {
    enabled: boolean;
    level: LogLevel;
}

// Valid log levels
const validLogLevels: LogLevel[] = ['silent', 'error', 'warn', 'info', 'debug', 'trace'];

// Validate if a string is a valid log level
const isValidLogLevel = (value: string): value is LogLevel => {
    return validLogLevels.includes(value as LogLevel);
};

// Defaults: silent unless explicitly enabled via env (avoids console noise on web/native).
const defaultConfig: LoggerConfig = {
    enabled: process.env.EXPO_PUBLIC_ENABLE_LOGS === 'true',
    level:
        process.env.EXPO_PUBLIC_LOG_LEVEL !== undefined &&
        isValidLogLevel(process.env.EXPO_PUBLIC_LOG_LEVEL)
            ? process.env.EXPO_PUBLIC_LOG_LEVEL
            : 'error',
};

let currentConfig: LoggerConfig = { ...defaultConfig };

const shouldLog = (level: LogLevel): boolean => {
    if (!currentConfig.enabled) return false;
    const order: LogLevel[] = ['silent', 'error', 'warn', 'info', 'debug', 'trace'];
    return order.indexOf(level) <= order.indexOf(currentConfig.level);
};

/**
 * Check if an object has nested objects that might not be fully printed by console.log
 */
function hasNestedObjects(obj: any, depth: number = 0): boolean {
    if (depth > 3) return false; // Limit recursion depth
    if (obj === null || obj === undefined || typeof obj !== 'object') {
        return false;
    }
    if (Array.isArray(obj)) {
        return obj.some(item => typeof item === 'object' && item !== null);
    }
    return Object.values(obj).some(value => typeof value === 'object' && value !== null);
}

function log(level: LogLevel, tag: string, ...args: any[]) {
    if (!shouldLog(level)) return;
    const prefix = `[${tag}]`;
    
    // Process arguments and log formatted JSON for nested objects
    const processedArgs: any[] = [];
    args.forEach(arg => {
        if (typeof arg === 'object' && arg !== null && hasNestedObjects(arg)) {
            // For objects with nested structures, log formatted JSON separately
            // This ensures nested objects are fully visible in React Native console
            try {
                const formatted = JSON.stringify(arg, null, 2);
                // Log the formatted JSON as a separate argument so it's fully visible
                processedArgs.push(formatted);
            } catch {
                processedArgs.push(arg);
            }
        } else {
            processedArgs.push(arg);
        }
    });
    
    switch (level) {
        case 'error':

            console.error(prefix, ...processedArgs);
            break;
        case 'warn':

            console.warn(prefix, ...processedArgs);
            break;
        case 'info':

            console.info(prefix, ...processedArgs);
            break;
        case 'debug':

            console.debug(prefix, ...processedArgs);
            break;
        case 'trace':

            console.trace(prefix, ...processedArgs);
            break;
        default:

            console.log(prefix, ...processedArgs);
    }
}

export const logger = {
    configure(config: Partial<LoggerConfig>) {
        currentConfig = { ...currentConfig, ...config };
    },
    error: (tag: string, ...args: any[]) => log('error', tag, ...args),
    warn: (tag: string, ...args: any[]) => log('warn', tag, ...args),
    info: (tag: string, ...args: any[]) => log('info', tag, ...args),
    debug: (tag: string, ...args: any[]) => log('debug', tag, ...args),
    trace: (tag: string, ...args: any[]) => log('trace', tag, ...args),
};

export type { LogLevel };


